import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, getDocs, writeBatch, doc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../../../assets/data/firebase";
import { apiServices } from "../../../services/apiServices";
import "../../../assets/css/Home.css";

import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, Avatar, Typography, Paper, Chip } from '@mui/material';

const GroupChat = ({ groupId: propGroupId, groupName: propGroupName, groupKey: propGroupKey }) => {
  const params = useParams();
  // Use props if provided, otherwise fall back to URL params
  const groupId = propGroupId || params.groupId;
  const groupName = propGroupName || params.groupName;
  const groupKey = propGroupKey || params.groupKey;
  
  // Use group_key for Firebase collection if available, otherwise fall back to groupId
  const firebaseGroupId = groupKey || groupKey;
  
  console.log('firebaseGroupId', params, groupKey, firebaseGroupId);
  // Get user data from Redux store
  const { user } = useSelector((state) => state.auth);
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!firebaseGroupId) return;

    const messagesRef = collection(db, "groupChats", firebaseGroupId, "messages");
    const q = query(messagesRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [firebaseGroupId]);

  // Update user activity when entering group chat
  useEffect(() => {
    const updateActivity = async () => {
      if (!firebaseGroupId || !user?.peacekeeper_id) return;
      
      try {
        // Extract group_id from firebaseGroupId (assuming it's the numeric ID)
        const groupId = firebaseGroupId.replace(/[^0-9]/g, '');
        if (groupId) {
          await apiServices.putAPI(`groups/${groupId}/activity`, {
            peacekeeperId: user.peacekeeper_id
          });
        }
      } catch (error) {
        console.error('Error updating activity:', error);
      }
    };

    updateActivity();
  }, [firebaseGroupId, user?.peacekeeper_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === "") return;

    // Use user ID from Redux persist instead of auth.currentUser
    const userId = user?.userid || user?.uid || "unknown";
    const userEmail = user?.username || user?.email || user?.name || "unknown";
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0].replace(/-/g, ''); // YYYYMMDD
    const sessionId = `${formattedDate}_${userId}`;

    try {
      const messagesRef = collection(db, "groupChats", firebaseGroupId, "messages");
      // Add user message
      const newMessageDoc = await addDoc(messagesRef, {
        text: newMessage,
        senderId: userId,
        senderEmail: userEmail,
        createdAt: serverTimestamp(),
      });

      setNewMessage("");

      // Send notifications to all users except sender using batch writes
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const batch = writeBatch(db);
        usersSnapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          if (userData.uid !== userId) {
            const notifRef = doc(collection(db, "notifications"));
            batch.set(notifRef, {
              userId: userData.uid,
              message: `New message in group chat: ${newMessage}`,
              read: false,
              createdAt: serverTimestamp(),
              groupId: firebaseGroupId,
              messageId: newMessageDoc.id,
            });
          }
        });
        await batch.commit();
      } catch (error) {
        console.error("Error sending notifications: ", error);
      }

    } catch (error) {
      console.error("Error sending message or calling API: ", error);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    // Use user ID from Redux persist instead of auth.currentUser
    const userId = user?.userid || user?.uid || "unknown";
    const storageRef = ref(storage, `groupChats/${firebaseGroupId}/${Date.now()}_${file.name}`);

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed', 
      (snapshot) => {
        // Optional: can handle progress here
      }, 
      (error) => {
        console.error("Upload failed:", error);
        setUploading(false);
      }, 
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        try {
          const messagesRef = collection(db, "groupChats", firebaseGroupId, "messages");
          await addDoc(messagesRef, {
            attachmentUrl: downloadURL,
            attachmentName: file.name,
            senderId: userId,
            senderEmail: user?.email || user?.username || user?.name || "unknown",
            createdAt: serverTimestamp(),
          });
        } catch (error) {
          console.error("Error sending attachment message: ", error);
        }
        setUploading(false);
      }
    );
  };

  const isImage = (url) => {
    return /\.(jpg|jpeg|png|gif|bmp|webp)(\?.*)?$/i.test(url);
  };

  // Helper function to format date as string
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  };

  // Helper function to format time as string
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.createdAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  // Use user ID from Redux persist instead of auth.currentUser
  const currentUserId = user?.userid || user?.uid;

  // If used within dashboard (no MobileHeader needed)
  if (propGroupId) {
    return (


      <>
       <Box sx={{ 
          p: 1, 
          bgcolor: 'white',
          borderBottom: 1,
          borderColor: 'divider',
          textAlign: 'center'
        }}>
          <Typography 
            variant="h5" 
            className='green-title'
            sx={{ 
              fontWeight: 'bold',
              fontSize: '1.5rem',
              mb: 0.5
            }}
          >
            {groupName}
          </Typography>
          
        </Box>
      
      
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: '#f8f9fa'
      }} className="chatAreaDiv">
        {/* Chat Messages Area */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 2,
          pb: '80px', // Add padding to prevent messages from being hidden behind fixed input
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          {Object.keys(groupedMessages).map(date => (
            <Box key={date} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Date Separator */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                my: 2 
              }}>
                <Chip 
                  label={date}
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(0,0,0,0.08)',
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
              
              {/* Messages for this date */}
              {groupedMessages[date].map(msg => {
                const isCurrentUser = msg.senderId === currentUserId;
                const isSystem = msg.senderId === "TIBA";
                
                return (
                  <Box key={msg.id} sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                    gap: 0.5
                  }}>
                    {/* Sender Info */}
                    {!isSystem && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        mb: 0.5,
                        ml: isCurrentUser ? 0 : 1,
                        mr: isCurrentUser ? 1 : 0
                      }}>
                        <Avatar 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            fontSize: '0.75rem',
                            bgcolor: isCurrentUser ? 'primary.main' : 'secondary.main'
                          }}
                        >
                          {msg.senderEmail.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography 
                          variant="" 
                          sx={{ 
                            fontWeight: 600,
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}
                          
                        >
                          {msg.senderEmail}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Message Content */}
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                      maxWidth: '70%'
                    }} className="msgChat">
                      {/* Text Message */}
                      {msg.text && (
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 1,
                            bgcolor: isCurrentUser 
                              ? 'primary.main' 
                              : isSystem 
                                ? 'warning.light'
                                : 'white',
                            color: isCurrentUser || isSystem ? 'white' : 'text.primary',
                            borderRadius: 3,
                            // borderTopLeftRadius: isCurrentUser ? 3 : 1,
                            // borderTopRightRadius: isCurrentUser ? 1 : 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            wordBreak: 'break-word',
                            position: 'relative'
                          }}
                        >
                          <Typography variant="body2" sx={{ lineHeight: 1.4 , mb:0 }}>
                            {msg.text}
                          </Typography>
                          
                          {/* Time */}
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              mt: 1,
                              opacity: 0.7,
                              fontSize: '0.6rem'
                            }}
                          >
                            {formatTime(msg.createdAt)}
                          </Typography>
                        </Paper>
                      )}
                      
                      {/* Base64 Image */}
                      {msg.base64Image && (
                        <Box sx={{ mt: 1 }}>
                          <img 
                            src={'data:image/png;base64,'+ msg.base64Image} 
                            alt="Message Image"
                            style={{
                              maxWidth: '100%',
                              borderRadius: 12,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}
                          />
                        </Box>
                      )}
                      
                      {/* File Attachment */}
                      {msg.attachmentUrl && (
                        <Box sx={{ mt: 1 }}>
                          {isImage(msg.attachmentUrl) ? (
                            <Box sx={{ position: 'relative' }}>
                              <img
                                src={msg.attachmentUrl}
                                alt={msg.attachmentName || "Attachment"}
                                style={{
                                  maxWidth: '100%',
                                  borderRadius: 12,
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}
                                onClick={() => setModalImage(msg.attachmentUrl)}
                              />
                            </Box>
                          ) : (
                            <Paper 
                              elevation={0}
                              sx={{ 
                                p: 2,
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <AttachFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {msg.attachmentName || "Attachment"}
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>
        
        {/* Message Input Area - Fixed at Extreme Bottom */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'white',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000
        }} className="chatBox">
          <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            {/* File Attachment Button */}
            {/* <Box 
              onClick={() => fileInputRef.current.click()}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'grey.100',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'grey.200'
                }
              }}
            >
              <AttachFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Box> */}
            
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={uploading}
            />
            
            {/* Message Input Field */}
            <Box sx={{ flex: 1 }}>
              <input
                type="text"
                placeholder={uploading ? "Uploading..." : "Type your message..."}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '24px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1976d2';
                  e.target.style.boxShadow = '0 0 0 2px rgba(25, 118, 210, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={uploading}
              />
            </Box>
            
            {/* Voice Message Button */}
            {/* <Box sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'grey.100',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'grey.200'
              }
            }}>
              <MicIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Box> */}
            
            {/* Send Button */}
            <button 
              type="submit" 
              disabled={uploading || !newMessage.trim()}
              style={{
                padding: '12px 16px',
                backgroundColor: newMessage.trim() ? '#1976d2' : '#e0e0e0',
                color: 'white',
                border: 'none',
                borderRadius: '24px',
                cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                minWidth: '48px'
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </button>
          </form>
        </Box>
        
        {/* Image Modal */}
        {modalImage && (
          <Box 
            onClick={() => setModalImage(null)}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              p: 2
            }}
          >
            <img 
              src={modalImage} 
              alt="Full Attachment" 
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: 8,
                objectFit: 'contain'
              }}
            />
          </Box>
        )}
      </Box>
      </>
    );
  }

  // Original standalone version with MobileHeader
  return (
    <div className="groupchat-container">
      <div className="chat-messages">
        {Object.keys(groupedMessages).map(date => (
          <div key={date} className="date-group">
            <div className="date-separator">{date}</div>
            {groupedMessages[date].map(msg => {
              const isCurrentUser = msg.senderId === currentUserId;
              return (
                <div key={msg.id} className={`chat-message ${isCurrentUser ? "right" : "left"}`}>
                  <div className="message-sender" style={{ fontWeight: "bold", marginBottom: "4px" }}>{msg.senderEmail}</div>
                  {msg.text && <div className="message-text" style={{ whiteSpace: "pre-wrap" }}>{msg.text}</div>}
                  {msg.base64Image && <div className="message-attachment"><img src={'data:image/png;base64,'+ msg.base64Image} alt="Base64 Image" /></div>}
                  {msg.attachmentUrl && (
                    <div className="message-attachment">
                      {isImage(msg.attachmentUrl) ? (
                        <>
                          <img
                            src={msg.attachmentUrl}
                            alt={msg.attachmentName || "Attachment"}
                            className="attachment-thumbnail"
                            onClick={() => setModalImage(msg.attachmentUrl)}
                            style={{ cursor: "pointer" }}
                          />
                          {modalImage && (
                            <div className="modal-overlay" onClick={() => setModalImage(null)}>
                              <div className="modal-content" onClick={e => e.stopPropagation()}>
                                <img src={modalImage} alt="Full Attachment" className="modal-image" />
                                <button className="modal-close-button" onClick={() => setModalImage(null)}>Close</button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <a href={msg.attachmentUrl} target="_blank" rel="noopener noreferrer">
                          {msg.attachmentName || "Attachment"}
                        </a>
                      )}
                    </div>
                  )}
                  <div className="message-time">{formatTime(msg.createdAt)}</div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <form className="message-form" onSubmit={handleSendMessage}>
        {/* <AttachFileIcon className="input-icon" onClick={() => fileInputRef.current.click()} style={{ cursor: "pointer" }} /> */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          disabled={uploading}
        />
        <input
          type="text"
          placeholder={uploading ? "Uploading..." : "Type your message..."}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className="message-input"
          disabled={uploading}
        />
        {/* <MicIcon className="input-icon" /> */}
        <button type="submit" className="send-button" disabled={uploading}>
          <SendIcon />
        </button>
      </form>
    </div>
  );
};

export default GroupChat;
